import type { GetOffersCargosParams } from "@/entities/get-offers-cargos/model";
import {
  OFFER_CARGO_VEHICLE_SHAPES_LIST,
  PACKING_STYLES_LIST,
  isOfferCargoVehicleShape,
  isPackagingStyle,
} from "@/shared/constants/offer-cargo";
import { BaseButton } from "@/shared/ui/BaseButton";
import { DateRangePicker } from "@/shared/ui/DateRangePicker";
import { BaseCheckbox } from "@/shared/ui/base-checkbox";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseNumberField } from "@/shared/ui/base-number-field";
import { BaseSelect } from "@/shared/ui/base-select";
import { PrefectureField } from "@/widgets/prefecture-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { MenuItem, Stack } from "@mui/material";
import type { MouseEvent } from "react";
import { useForm } from "react-hook-form";
import {
  OfferCargoSearchFormSchema,
  type OfferCargoSearchFormSchemaType,
} from "./schema";

const LocationSelect = ({
  label,
  prefecture,
  onPrefectureChange,
}: {
  label: string;
  prefecture: string | undefined;
  onPrefectureChange: (prefecture: string | undefined) => void;
}) => (
  <>
    <BaseFormLabel label={`${label}(都道府県)`}>
      <PrefectureField
        value={prefecture}
        size="small"
        onChange={(v) => onPrefectureChange(v)}
      />
    </BaseFormLabel>
  </>
);

export type SearchFormCardProps = {
  onGetOffersCargosParamsChange: (
    params: Pick<
      GetOffersCargosParams,
      | "loadingOnFrom"
      | "loadingOnTo"
      | "loadingPrefecture"
      | "unloadingPrefecture"
      | "packingStyle"
      | "vehicleShape"
      | "shippingFee"
    >,
  ) => void;
  onGetOffersCargosParamsReset: () => void;
  onIsNewFilterChange: (isNew: boolean | undefined) => void;
};

export const SearchFormCard = ({
  onGetOffersCargosParamsChange,
  onGetOffersCargosParamsReset,
  onIsNewFilterChange,
}: SearchFormCardProps) => {
  const { handleSubmit, setValue, watch, reset } =
    useForm<OfferCargoSearchFormSchemaType>({
      resolver: zodResolver(OfferCargoSearchFormSchema),
    });
  const currentValue = watch();
  const handleSearchClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleSubmit((data) => {
      onGetOffersCargosParamsChange({
        loadingOnFrom: data.loadingScheduleStart,
        loadingOnTo: data.loadingScheduleEnd,
        loadingPrefecture: data.loadingLocationPrefecture,
        unloadingPrefecture: data.unloadingLocationPrefecture,
        packingStyle: data.packingStyle,
        vehicleShape: data.vehicleShape,
        shippingFee: data.shippingFee,
      });
      onIsNewFilterChange(data.isNew);
    })();
  };
  const handleResetClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onGetOffersCargosParamsReset();
    reset();
  };

  return (
    <Stack
      p={3}
      spacing={3}
      borderRadius={"18px"}
      boxShadow="0px 1px 10px 0px #0000001A"
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={3}>
          <DateRangePicker
            label="積日"
            startDate={currentValue.loadingScheduleStart}
            endDate={currentValue.loadingScheduleEnd}
            onStartDateChange={(date) => setValue("loadingScheduleStart", date)}
            onEndDateChange={(date) => setValue("loadingScheduleEnd", date)}
          />
          <LocationSelect
            label="積地"
            prefecture={currentValue.loadingLocationPrefecture}
            onPrefectureChange={(prefecture) =>
              setValue("loadingLocationPrefecture", prefecture)
            }
          />
          <LocationSelect
            label="卸地"
            prefecture={currentValue.unloadingLocationPrefecture}
            onPrefectureChange={(prefecture) =>
              setValue("unloadingLocationPrefecture", prefecture)
            }
          />
        </Stack>
        <Stack direction="row" spacing={3}>
          <BaseFormLabel label={"荷姿"}>
            <BaseSelect
              value={currentValue.packingStyle}
              size="small"
              type="string"
              noSelectedOptionText="(選択解除)"
              sx={{ minWidth: 256 }}
              onChange={(v) => {
                setValue("packingStyle", isPackagingStyle(v) ? v : undefined);
              }}
            >
              {PACKING_STYLES_LIST.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </BaseSelect>
          </BaseFormLabel>
          <BaseFormLabel label={"ボディタイプ"}>
            <BaseSelect
              value={currentValue.vehicleShape}
              size="small"
              type="string"
              noSelectedOptionText="(選択解除)"
              sx={{ minWidth: 256 }}
              onChange={(v) => {
                setValue(
                  "vehicleShape",
                  isOfferCargoVehicleShape(v) ? v : undefined,
                );
              }}
            >
              {OFFER_CARGO_VEHICLE_SHAPES_LIST.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </BaseSelect>
          </BaseFormLabel>
          <BaseFormLabel label={"運賃"}>
            <BaseNumberField
              value={currentValue.shippingFee}
              size="small"
              unit="円以上"
              sx={{ minWidth: 128 }}
              onChange={(value) => {
                setValue("shippingFee", value === "" ? undefined : value);
              }}
            />
          </BaseFormLabel>
        </Stack>
        <Stack direction="row" spacing={3} pt={1}>
          <BaseCheckbox
            label="新着のみ"
            value={currentValue.isNew ?? false}
            onChange={(v) => {
              setValue("isNew", v);
            }}
          />
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3} mt={1}>
        <BaseButton variant="contained" onClick={handleSearchClick}>
          絞り込む
        </BaseButton>
        <BaseButton variant="outlined" onClick={handleResetClick}>
          リセット
        </BaseButton>
      </Stack>
    </Stack>
  );
};
